import type {
  LayoutComponent,
  PreparedRoute,
  PreparedSiteEnvelope,
} from "@brains/site-engine";
import { sha256Hex } from "@brains/utils/hash";
import { randomUUID } from "crypto";
import { isPlainRecord } from "@brains/utils/predicates";
import type { SiteBuilderServices } from "./site-builder-services";
import type { SiteViewTemplate } from "./site-view-template";
import type { StaticSiteBuilderFactory } from "./static-site-builder";

/** Renderer code cannot change while the app runs, but a restart — an
 *  upgrade included — may bring new components, styles or scripts that
 *  `String(fn)` cannot see. A fresh identity per process renders once
 *  after every start, and skips unchanged builds while the app runs. */
export const RENDERER_PROCESS_IDENTITY: string = randomUUID();

export interface SiteInputFingerprintOptions {
  /** The build's fields besides its pages. */
  envelope: PreparedSiteEnvelope;
  /** Each page's renderer input as a hash, in route order. */
  routeDigests: readonly string[];
  /** The templates the pages' sections use. */
  templates: readonly string[];
  layouts: Record<string, LayoutComponent>;
  getViewTemplate(name: string): SiteViewTemplate | undefined;
  staticSiteBuilderFactory: StaticSiteBuilderFactory;
  sendMessage: SiteBuilderServices["sendMessage"];
  rendererIdentity: string;
}

/** One page's renderer input as a hash. A site's fingerprint is built from
 *  its pages' hashes, so no build is ever serialized whole: a book brain's
 *  thousands of pages would otherwise make one string as large as the build,
 *  several times over. */
export function fingerprintPreparedRoute(route: PreparedRoute): string {
  return sha256Hex(stableSerialize(route));
}

/** Hash all serializable renderer inputs, the selected renderer functions and
 *  the renderer process identity. Functions are hashed by source text
 *  (`String(fn)`), which cannot see closure-captured state — a renderer must
 *  derive its output from the prepared build and its code, never from
 *  captured mutable data, or an unchanged fingerprint could skip a build
 *  whose output would differ. */
export function computeSiteInputFingerprint(
  options: SiteInputFingerprintOptions,
): string {
  const {
    buildId: _buildId,
    preparedAt: _preparedAt,
    ...preparedInput
  } = options.envelope;
  const templateNames = [...new Set(options.templates)].sort();

  return sha256Hex(
    stableSerialize({
      version: 4,
      rendererIdentity: options.rendererIdentity,
      preparedInput: {
        ...preparedInput,
        routes: options.routeDigests,
      },
      layouts: Object.fromEntries(
        Object.entries(options.layouts)
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([name, layout]) => [name, String(layout)]),
      ),
      rendererRuntime: {
        staticSiteBuilderFactory: String(options.staticSiteBuilderFactory),
        stagingMessageSender: String(options.sendMessage),
      },
      templates: Object.fromEntries(
        templateNames.map((name) => {
          const template = options.getViewTemplate(name);
          return [
            name,
            template
              ? {
                  renderer: String(template.renderers.web),
                  fullscreen: template.fullscreen ?? false,
                  runtimeScripts: template.runtimeScripts ?? [],
                  staticAssets: template.staticAssets ?? {},
                }
              : null,
          ];
        }),
      ),
    }),
  );
}

function stableSerialize(
  value: unknown,
  ancestors: Set<object> = new Set(),
): string {
  if (value === null) return "null";
  if (typeof value === "string" || typeof value === "boolean") {
    return JSON.stringify(value);
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value))
      throw new Error("Cannot fingerprint non-finite number");
    return JSON.stringify(value);
  }
  if (typeof value === "function") return JSON.stringify(String(value));
  if (typeof value !== "object") {
    throw new Error(`Cannot fingerprint ${typeof value} value`);
  }
  if (ancestors.has(value)) {
    throw new Error("Cannot fingerprint circular site inputs");
  }

  const nextAncestors = new Set(ancestors).add(value);
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item, nextAncestors)).join(",")}]`;
  }
  if (!isPlainRecord(value)) {
    throw new Error("Cannot fingerprint non-plain site input objects");
  }

  return `{${Object.entries(value)
    .filter(([, child]) => child !== undefined)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(
      ([key, child]) =>
        `${JSON.stringify(key)}:${stableSerialize(child, nextAncestors)}`,
    )
    .join(",")}}`;
}
